/**
 * « Ma progression » — states, and not one number (invariant 1, web-pwa §5.1).
 *
 * Two rules meet on this screen. A child sees their competencies change state,
 * never a score on themselves; and a child sees **their own** progression, never
 * the other one's (CLAUDE.md rule 4) — which is why nothing here takes a child
 * identifier: the session already says who is asking.
 *
 * **Three groups, and no total** (invariant 12). Not a percentage, not "12 of
 * 58", not a per-subject average. `exercise/internal/rubric.py` says why: a
 * scale invites a total, and a total is the mark out of twenty coming back in
 * through the side door. A child computes the ratio the moment you hand them
 * both terms.
 *
 * **A state is never taken away** (invariant 13). `mastery` carries a forgetting
 * curve, so a competency falls due without the child having done anything wrong.
 * Saying that with a track that slides backwards reads as a punishment — and the
 * contract forbids putting slowness on trial. The two axes are kept apart: the
 * track says what was *reached*, the "à revoir" group says what needs *a pass*.
 *
 * **« Ma semaine » en tête** (SPEC §12) : les compétences passées en acquis ou
 * consolidé ces sept derniers jours, par matière, avec une phrase. Des noms et
 * un mot d'état, jamais un compte — « trois cette semaine » appelle « et la
 * semaine dernière ? », et c'est la comparaison que l'invariant 1 refuse. Une
 * lecture en panne retire la carte au lieu de fermer l'écran : elle s'ajoute à
 * la progression, elle ne la conditionne pas.
 *
 * **Au lycée, les chiffres** (§14.1, assoupli le 29 septembre 2026 pour le
 * lycée seulement). Quand `profile.ownNumbers` est vrai **et** que le serveur a
 * envoyé `chiffres`/`activite`, l'écran ajoute la moyenne pondérée, le score de
 * chaque matière et de chaque compétence mesurée, et les exercices faits et
 * réussis du premier coup. Les deux conditions et pas une : le serveur ne les
 * envoie qu'au lycée, et un écran de collège ne les dessinerait pas même s'ils
 * arrivaient. Tout ce qui précède reste vrai au collège.
 *
 * Fluency is called *automatismes*. Never "vitesse": Baya is slow and knows it,
 * and the interface is not going to be one more place that says so.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import type {
  ActiviteDeMatiere,
  ChildProgression,
  CompetencyBadge,
  MaSemaine,
  MesChiffres,
  MonActivite,
} from "@/api/schema";
import { useSession } from "@/app/session";
import {
  REPERE_DU_SCORE,
  ligneDActivite,
  ligneDeLaMoyenne,
  scoreSur100,
  scoresParCompetence,
} from "@/child/chiffres";
import type { Tone } from "@/config/profile";
import {
  FLUENCY_CHILD_HINT,
  FLUENCY_CHILD_LABEL,
  WEEK_EMPTY,
  WEEK_LEAD,
  WEEK_STATE_WORD,
  subjectLabel,
} from "@/config/wording";
import { Gauge } from "@/ui/Gauge";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { StateTrack } from "@/ui/StateChip";

/** Group a list by subject, keeping the order the server sent. */
export function bySubject(badges: CompetencyBadge[]): [string, CompetencyBadge[]][] {
  const groups = new Map<string, CompetencyBadge[]>();
  for (const badge of badges) {
    const found = groups.get(badge.subject);
    if (found) found.push(badge);
    else groups.set(badge.subject, [badge]);
  }
  return [...groups];
}

function Group({
  title,
  lead,
  badges,
  empty,
  scores,
}: {
  title: string;
  lead: string;
  badges: CompetencyBadge[];
  empty: string;
  /** Lycée seulement : le score des compétences mesurées. Absent au collège. */
  scores?: Map<string, number>;
}) {
  return (
    <Card title={title}>
      <p className="muted small">{lead}</p>
      {badges.length === 0 ? (
        <Empty>{empty}</Empty>
      ) : (
        bySubject(badges).map(([subject, list]) => (
          <section key={subject} className="progress__subject">
            <h4>{subjectLabel(subject)}</h4>
            <ul className="progress__list">
              {list.map((badge) => (
                <li key={badge.competency_id}>
                  <span className="progress__label">{badge.label}</span>
                  {scores?.has(badge.competency_id) ? (
                    <span className="muted small">
                      {scoreSur100(scores.get(badge.competency_id) ?? 0)}
                    </span>
                  ) : null}
                  <StateTrack state={badge.state} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </Card>
  );
}

function Activite({ titre, lignes }: { titre: string; lignes: ActiviteDeMatiere[] }) {
  return (
    <section className="progress__subject">
      <h4>{titre}</h4>
      {lignes.length === 0 ? (
        <p className="muted small">Aucun exercice fait.</p>
      ) : (
        <ul className="progress__list">
          {lignes.map((ligne) => (
            <li key={ligne.subject}>
              <span className="progress__label">{ligne.subject_label}</span>
              <span className="muted small">{ligneDActivite(ligne)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * « Ma semaine » : ce qui a passé un cap, par matière. Sans un chiffre au
 * collège ; au lycée, `activite` ajoute les exercices faits et réussis.
 */
export function SemaineCard({
  semaine,
  tone,
  activite,
}: {
  semaine: MaSemaine;
  tone: Tone;
  /** Passé seulement quand le profil a droit à ses chiffres. */
  activite?: MonActivite | null;
}) {
  const matieres = (semaine.subjects ?? []).filter((m) => (m.competencies ?? []).length > 0);
  return (
    <Card title="Ma semaine">
      {activite ? (
        <>
          <Activite titre="Ces sept derniers jours" lignes={activite.semaine ?? []} />
          <Activite titre="Depuis la rentrée" lignes={activite.depuis_la_rentree ?? []} />
          <p className="muted small">
            Réussi du premier coup : la seconde tentative vient après le corrigé, elle ne
            compte pas.
          </p>
        </>
      ) : null}
      {matieres.length === 0 ? (
        <Empty>{WEEK_EMPTY[tone]}</Empty>
      ) : (
        <>
          <p className="muted small">{WEEK_LEAD[tone]}</p>
          {matieres.map((matiere) => (
            <section key={matiere.subject} className="progress__subject">
              <h4>{subjectLabel(matiere.subject)}</h4>
              <ul className="progress__list">
                {(matiere.competencies ?? []).map((competence) => (
                  <li key={competence.competency_id}>
                    <span className="progress__label">{competence.label}</span>
                    <span className="muted small">{WEEK_STATE_WORD[competence.state]}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </Card>
  );
}

/** Lycée seulement : la moyenne pondérée et le score de chaque matière. */
export function ChiffresCard({ chiffres }: { chiffres: MesChiffres }) {
  const ensemble = chiffres.ensemble;
  const matieres = chiffres.matieres ?? [];
  return (
    <Card title="Mes chiffres">
      <p className="muted small">{REPERE_DU_SCORE}</p>
      {ensemble ? (
        <section className="progress__subject">
          <h4>Moyenne — {scoreSur100(ensemble.score)}</h4>
          <Gauge name="Moyenne" score={ensemble.score} observations={ensemble.observations} />
          {/* Hors de la jauge, qui remplace sa ligne du bas par sa prudence en
              dessous de cinq observations : la pondération se dit toujours. */}
          <p className="muted small">{ligneDeLaMoyenne(ensemble)}</p>
        </section>
      ) : null}
      {matieres.map((matiere) => (
        <section key={matiere.subject} className="progress__subject">
          <h4>
            {matiere.label} — {scoreSur100(matiere.score)}
          </h4>
          <Gauge name={matiere.label} score={matiere.score} observations={matiere.observations} />
        </section>
      ))}
    </Card>
  );
}

export function ProgressScreen() {
  const { profile } = useSession();
  const [progression, setProgression] = useState<ChildProgression | null>(null);
  const [failed, setFailed] = useState(false);
  const [semaine, setSemaine] = useState<MaSemaine | null>(null);

  useEffect(() => {
    let alive = true;
    call("myProgression")
      .then((data) => alive && setProgression(data))
      .catch(() => alive && setFailed(true));
    // Une panne ici laisse `semaine` à null : la carte ne s'affiche pas, le
    // reste de l'écran si.
    call("maSemaine")
      .then((data) => alive && setSemaine(data))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  if (failed) {
    return (
      <Screen title="Ma progression" lead="Où en sont tes compétences.">
        <ErrorNote>
          Je n'arrive pas à afficher ta progression pour l'instant. Ça n'a rien à voir avec
          ton travail — réessaie tout à l'heure.
        </ErrorNote>
      </Screen>
    );
  }

  if (progression === null) {
    return (
      <Screen title="Ma progression" lead="Où en sont tes compétences.">
        <Loading />
      </Screen>
    );
  }

  const acquis = progression.acquis ?? [];
  const enCours = progression.en_cours ?? [];
  const aRevoir = progression.a_revoir ?? [];
  const jamais = progression.non_travaille ?? 0;
  const rien = acquis.length === 0 && enCours.length === 0 && aRevoir.length === 0;
  // Deux conditions : le serveur n'envoie les chiffres qu'au lycée, et l'écran
  // ne les dessine qu'au lycée.
  const chiffres = profile.ownNumbers ? (progression.chiffres ?? null) : null;
  const scores = chiffres ? scoresParCompetence(chiffres) : undefined;

  return (
    <Screen title="Ma progression" lead="Où en sont tes compétences.">
      {semaine ? (
        <SemaineCard
          semaine={semaine}
          tone={profile.tone}
          activite={profile.ownNumbers ? (semaine.activite ?? null) : null}
        />
      ) : null}

      {chiffres ? <ChiffresCard chiffres={chiffres} /> : null}

      <Card title="Comment ça se lit">
        <StateTrack state="en_cours" />
        <p className="muted small">
          Une compétence avance quand tu la travailles. Quand ça fait longtemps, elle passe
          dans « à revoir bientôt » — ça ne retire rien à ce que tu as déjà appris, c'est
          juste le moment d'y repasser.
        </p>
        <p className="muted small">
          Et les exercices sont réglés pour qu'environ un sur cinq résiste. C'est fait exprès :
          si tout passait, ils seraient trop faciles pour toi.
        </p>
      </Card>

      {rien ? (
        <Card title="Tes matières">
          <Empty>
            Rien à montrer pour l'instant. Tes compétences apparaîtront ici dès que le tuteur
            en aura observé.
          </Empty>
        </Card>
      ) : (
        <>
          <Group
            title="À revoir bientôt"
            lead="Ce sur quoi il est temps de repasser."
            badges={aRevoir}
            scores={scores}
            empty="Rien à revoir dans l'immédiat."
          />
          <Group
            title="En cours"
            lead="Ce que tu es en train d'apprendre."
            badges={enCours}
            scores={scores}
            empty="Rien en cours pour le moment."
          />
          <Group
            title="Acquis"
            lead="Ce que tu sais faire."
            badges={acquis}
            scores={scores}
            empty="Rien encore — ça viendra."
          />
        </>
      )}

      {jamais > 0 && (
        <Card title="Pas encore travaillé">
          <p className="muted small">
            {/* Un décompte de ce qui n'a pas été regardé, et qui ne se rapporte à rien :
                ce n'est pas un score, et surtout pas un bas d'échelle. `mastery` sort
                `non_evalue` plutôt que `fragile` pour cette raison — afficher une
                fragilité faute de données ferait passer « je ne sais pas » pour « c'est
                mauvais ». */}
            {jamais === 1
              ? "Une compétence que le tuteur n'a pas encore regardée."
              : `${jamais} compétences que le tuteur n'a pas encore regardées.`}
          </p>
        </Card>
      )}

      <Card title={FLUENCY_CHILD_LABEL}>
        <p className="muted small">{FLUENCY_CHILD_HINT}.</p>
      </Card>
    </Screen>
  );
}
